/**
 * Custom Quote Node for Lexical
 * Extends QuoteNode with blockId and direction support for AST compatibility
 */

import { QuoteNode, SerializedQuoteNode } from '@lexical/rich-text'
import { $applyNodeReplacement, DOMConversionMap, DOMExportOutput, EditorConfig, LexicalNode, Spread } from 'lexical'

export type SerializedCustomQuoteNode = Spread<
  {
    blockId: string
    dir?: 'ltr' | 'rtl'
  },
  SerializedQuoteNode
>

export class CustomQuoteNode extends QuoteNode {
  __blockId: string

  static getType(): string {
    return 'custom-quote'
  }

  static clone(node: CustomQuoteNode): CustomQuoteNode {
    const cloned = new CustomQuoteNode(node.__blockId, node.__key)
    cloned.__dir = node.__dir
    cloned.__format = node.__format
    cloned.__indent = node.__indent
    return cloned
  }

  constructor(blockId: string, key?: string) {
    super(key)
    this.__blockId = blockId
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getDir(): 'ltr' | 'rtl' | null {
    return this.__dir
  }

  setDir(dir: 'ltr' | 'rtl' | null): this {
    const writable = this.getWritable()
    writable.__dir = dir
    // Keep Lexical's standard direction in sync so element.getDirection() (used by
    // the toolbar and the reconciler) reflects the same value. Otherwise __dir and
    // __direction can diverge — the DOM renders one way but Lexical thinks another.
    writable.setDirection(dir)
    return writable
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      dom.setAttribute('dir', this.__dir)
      dom.style.direction = this.__dir
      dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
    }
    return dom
  }

  updateDOM(prevNode: QuoteNode, dom: HTMLElement): boolean {
    const prevCustom = prevNode as CustomQuoteNode
    if (prevCustom.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevCustom.__dir !== this.__dir) {
      if (this.__dir) {
        dom.setAttribute('dir', this.__dir)
        dom.style.direction = this.__dir
        dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
      } else {
        dom.removeAttribute('dir')
        dom.style.direction = ''
        dom.style.textAlign = ''
      }
    }
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      blockquote: () => ({
        conversion: (element: HTMLElement) => {
          const blockId = element.getAttribute('data-block-id') || ''
          const dir = element.getAttribute('dir') as 'ltr' | 'rtl' | null
          const node = $createCustomQuoteNode(blockId)
          if (dir) {
            node.setDir(dir)
          }
          return { node }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('blockquote')
    element.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      element.setAttribute('dir', this.__dir)
    }
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomQuoteNode): CustomQuoteNode {
    const node = $createCustomQuoteNode(serializedNode.blockId)
    if (serializedNode.dir) {
      node.setDir(serializedNode.dir)
    }
    node.setFormat(serializedNode.format)
    node.setIndent(serializedNode.indent)
    return node
  }

  exportJSON(): SerializedCustomQuoteNode {
    return {
      ...super.exportJSON(),
      type: 'custom-quote',
      blockId: this.__blockId,
      dir: this.__dir || undefined,
    }
  }
}

export function $createCustomQuoteNode(blockId: string): CustomQuoteNode {
  return $applyNodeReplacement(new CustomQuoteNode(blockId))
}

export function $isCustomQuoteNode(node: LexicalNode | null | undefined): node is CustomQuoteNode {
  return node instanceof CustomQuoteNode
}
